import { ref, computed } from "vue";
import { UserAddress } from "../types/UserAddress.type";
import { useToast } from "vue-toastification";
import dateFormatters from "../formatters/dateFormatters";
import { StaticMessages } from "../enums/static-messages.enum";
import { LocationQueryValue } from "vue-router";
import router from "/@/router";
import store from "/@/store";
import { UserContact } from "../types/UserContact";
import { boardedStorage } from "./useBoardedStorage";

export default function useUserData() {
  const toast = useToast();
  const authUser = computed(() => store.getters["user/getAuthUser"]);
  const storage = boardedStorage;

  const isOnboarded = () => storage.value.boarded;

  const setOnboarded = (value: boolean) => {
    storage.value.boarded = value;
  };

  const registerUser = async (data: any) => {
    try {
      const res = await store.dispatch("user/register", data);
      toast.success(StaticMessages.REGISTRATION_SUCCESSFUL);
      router.push({ name: "login" });

      return res;
    } catch (error: any) {
      // Safe error handling - network errors don't have response object
      const message = error.response?.data?.message
        || error.message
        || 'Registration failed. Please try again.';
      toast.error(message);
    }
  };

  const getUserPhoneTypes = async () => {
    try {
      const res = await store.dispatch("user/getUserPhoneTypes");
      return res;
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  const getUserAddressTypes = async () => {
    try {
      const res = await store.dispatch("user/getUserAddressTypes");
      return res;
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  const getUserAddressBook = async () => {
    try {
      const res = await store.dispatch("user/getUserAddressBook");
      return res;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const states = ref([]);
  const countries = ref([]);

  const getAuthUser = async () => {
    try {
      // store.dispatch("miscData/showLoadingScreen");

      const res = await store.dispatch("user/getAuthUser");

      setOnboarded(notOnboarded.value ? false : true);

      // store.dispatch("miscData/stopLoadingScreen");

      return res;
    } catch (err: any) {
      return Promise.reject(err);
    }
  };

  const getStatesByCountry = async (countryName: string) => {
    try {
      const res = await store.dispatch("user/getCountriesAndStates");
      states.value = res.country_states;

      return states.value
        .filter(({ country }) => {
          return country == countryName;
        })
        .map(({ states }) => {
          return states;
        })[0];
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const getCountriesAndStates = async () => {
    try {
      const res = await store.dispatch("user/getCountriesAndStates");
      countries.value = res.country_states;

      return countries.value.map(({ country }) => {
        return country;
      });
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  const getAllCountries = async () => {
    try {
      const res = await store.dispatch("user/getAllCountries");

      return res;
    } catch (error: any) {
      return Promise.reject(error);
    }
  };

  const { formatDate } = dateFormatters();

  const postNewUserAddress = async (payload: UserAddress) => {
    // payload.startDate = formatDate(payload.startDate, "YYYYMMDD");
    // payload.stopDate = formatDate(payload.startDate, "YYYYMMDD");
    try {
      const res = await store.dispatch("user/postNewUserAddress", payload);
      toast.success(StaticMessages.NEW_ADDRESS_SUCCESS);

      await getAuthUser();
      await getUserAddressBook();

      return res;
    } catch (err: any) {
      toast.error(err.response.data.description);

      return Promise.reject(err);
    }
  };

  const getUserContacts = async () => {
    try {
      const res = await store.dispatch("user/getUserContacts");

      return res;
    } catch (err: any) {
      toast.error(err.response.data.message);

      return Promise.reject(err);
    }
  };

  const getPaymentTypes = async () => {
    try {
      const res = await store.dispatch("user/getPaymentTypes");

      return res;
    } catch (err: any) {
      toast.error(err.response.data.message);

      return Promise.reject(err);
    }
  };

  const postUserContact = async (payload: UserContact) => {
    try {
      const res = await store.dispatch("user/postUserContact", payload);

      toast.success(StaticMessages.NEW_CONTACT_SUCCESS);

      const [user, userContacts] = await Promise.all([
        getAuthUser(),
        getUserContacts(),
      ]);

      return res;
    } catch (err: any) {
      toast.error(err.response.data.message);

      return Promise.reject(err);
    }
  };

  const setContactAsPrimary = async (contactId: number | string) => {
    try {
      const res = await store.dispatch("user/setContactAsPrimary", contactId);

      toast.success(StaticMessages.CONTACT_SET_AS_PRIMRAY);

      await getUserContacts();

      return res;
    } catch (err: any) {
      toast.error(err.response.data.message);

      return Promise.reject(err);
    }
  };

  const forgotPassword = async (form: { eeni: string }) => {
    try {
      const res = await store.dispatch("user/forgotPassword", form);

      toast.success(StaticMessages.FORGOT_PASSWORD_LINK);

      return res;
    } catch (err: any) {
      toast.error(err.response.data.message);

      return Promise.reject(err);
    }
  };

  const resetUserPassword = async (data: {
    password: string;
    token: LocationQueryValue | LocationQueryValue[];
  }) => {
    try {
      const res = await store.dispatch("user/resetUserPassword", data);

      toast.success(StaticMessages.PASSWORD_RESET_SUCCESS);

      return res.data;
    } catch (err: any) {
      toast.error(err.response.data.message);

      return Promise.reject(err);
    }
  };

  const resendActivationEmail = async (data: { eeni: string }) => {
    try {
      const res = await store.dispatch("user/resendActivationEmail", data);

      toast.success(StaticMessages.ACTIVATION_LINK_RESENT);

      return res.data;
    } catch (err: any) {
      toast.error(err.response.data.message);

      return Promise.reject(err);
    }
  };

  const activateUserAccount = async (token: string) => {
    try {
      const res = await store.dispatch("user/activateUserAccount", token);

      toast.success(StaticMessages.ACCOUNT_ACTIVATION_SUCCESS);

      return res.data;
    } catch (err: any) {
      toast.error(err.response.data.message);

      return Promise.reject(err);
    }
  };

  const getUserBankAccounts = async () => {
    try {
      const res = await store.dispatch("user/getUserBankAccounts");

      return res.data;
    } catch (err: any) {
      return Promise.reject(err);
    }
  };

  const addUserBankAccount = async (payload: any) => {
    try {
      const res = await store.dispatch("user/addUserBankAccount", payload);

      toast.success(StaticMessages.BANK_ACCOUNT_ADDED_SUCCESS);

      return res.data;
    } catch (err: any) {
      toast.error(err.response.data.message);

      return Promise.reject(err);
    }
  };

  const getLocalOffices = async () => {
    try {
      const res = await store.dispatch("user/getLocalOffices");

      return res.data;
    } catch (err: any) {
      return Promise.reject(err);
    }
  };

  const changeLocalOffice = async (localOffice: any) => {
    try {
      const res = await store.dispatch("user/changeLocalOffice", localOffice);

      toast.success(StaticMessages.LOCAL_OFFICE_CHANGED_SUCCESS);

      await getAuthUser();

      return res.data;
    } catch (err: any) {
      toast.error(err.response.data.description);

      return Promise.reject(err);
    }
  };

  const changeUserPassword = async (payload: { password: string }) => {
    try {
      const res = await store.dispatch("user/changeUserPassword", payload);

      toast.success(StaticMessages.PASSWORD_CHANGED_SUCCESS);

      return res;
    } catch (err: any) {
      toast.error(err.response.data.description);

      return Promise.reject(err);
    }
  };

  const changeUserEmail = async (payload: { email: string }) => {
    try {
      const res = await store.dispatch("user/changeUserEmail", payload);

      toast.success(StaticMessages.EMAIL_CHANGE_REQUESTED);

      return res;
    } catch (err: any) {
      toast.error(err.response.data.message);

      return Promise.reject(err);
    }
  };

  const updateUserEmail = async (payload: { token: string }) => {
    try {
      const res = await store.dispatch("user/updateUserEmail", payload);

      toast.success(StaticMessages.EMAIL_CHANGED_SUCCESS);

      return res;
    } catch (err: any) {
      toast.error(err.response.data.message);

      return Promise.reject(err);
    }
  };

  const notOnboarded = computed(() => {
    return (
      !authUser.value.address ||
      !authUser.value.contact ||
      !authUser.value.local_office
    );
  });

  return {
    registerUser,
    getUserPhoneTypes,
    getUserAddressBook,
    getUserAddressTypes,
    getStatesByCountry,
    getCountriesAndStates,
    getPaymentTypes,
    postNewUserAddress,
    forgotPassword,
    postUserContact,
    getAllCountries,
    getUserContacts,
    resetUserPassword,
    resendActivationEmail,
    activateUserAccount,
    getUserBankAccounts,
    addUserBankAccount,
    getLocalOffices,
    getAuthUser,
    changeLocalOffice,
    changeUserPassword,
    changeUserEmail,
    updateUserEmail,
    notOnboarded,
    isOnboarded,
    setOnboarded,
    setContactAsPrimary,
    authUser,
  };
}
